import * as React from 'react';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import {
  MdAdd,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdEdit,
  MdPauseCircle,
  MdPeople,
  MdSearch,
  MdVisibility,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { APPRoutes } from '../../Utils/Route';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviders, deleteProvider } from '../../Services/ProviderService';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';

const STATUS_OPTIONS = ['All', 'Active', 'Inactive', 'Complete', 'Pending', 'Ineligible', 'Suspended', 'Unknown'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
  Complete: { backgroundColor: 'var(--hc-status-neutral-bg)', color: 'var(--hc-table-footer-text)' },
  Pending: { backgroundColor: 'var(--hc-status-pending-alt-bg)', color: 'var(--hc-blue-700)' },
  Ineligible: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
  Suspended: { backgroundColor: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)' },
  Unknown: { backgroundColor: 'var(--hc-status-unknown-bg)', color: 'var(--hc-status-unknown-text)' },
};

const getProviderName = (row) => {
  if (!row) return '';
  
  if (row.ProviderName || row.Name || row.Provider?.Name || row.Provider?.ProviderName || row.Provider?.FullName) {
    return row.ProviderName || row.Name || row.Provider?.Name || row.Provider?.ProviderName || row.Provider?.FullName;
  }

  const parts = [
    row.prefix_name || row.prefixName,
    row.first_name,
    row.middle_name,
    row.last_name,
    row.suffix_name || row.suffixName,
  ]
    .filter((part) => typeof part === 'string' && part.trim())
    .map((part) => part.trim());

  return parts.length ? parts.join(' ') : '';
};

const getOrganizationName = (row) =>
  row.OrganizationName || row.organization_name || row.Organization?.Name || row.Organization?.OrganizationName || '';

const getClientName = (row) =>
  row.ClientName || row.account_name || row.client_name || row.Client?.Name || row.Client?.ClientName || '';

const getStatusName = (row) => {
  if (!row) return 'Unknown';

  const rawStatus =
    row.status_name ||
    row.StatusName ||
    row.Status ||
    row.status ||
    row.Provider?.StatusName ||
    row.Provider?.status_name ||
    row.Provider?.Status;

  if (rawStatus && typeof rawStatus === 'string') {
    const trimmed = rawStatus.trim();
    // Exclude prefixes if backend mis-aliased
    if (!['Mr.', 'Mrs.', 'Ms.', 'Dr.'].includes(trimmed)) {
      return trimmed;
    }
  }

  const statusId = row.status_id ?? row.StatusId ?? row.statusId;
  if (statusId === 1 || statusId === '1') return 'Active';
  if (statusId === 2 || statusId === '2') return 'Inactive';

  return 'Unknown';
};

const getProviderRowId = (row) => row.Id ?? row.ProviderId ?? row.id ?? row.provider_id ?? row.providerId ?? null;

export default function ProvidersList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );
  const canViewProvider = permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_VIEW, decodedToken);
  const canCreateProvider = permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_CREATE, decodedToken);
  const canEditProvider = permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_EDIT, decodedToken);
  const canDeleteProvider = permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_DELETE, decodedToken);

  const isSuperAdmin = isSuperAdminUser(decodedToken);
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const handleAddProvider = React.useCallback(() => {
    if (canCreateProvider) {
      navigate(APPRoutes.PROVIDERS_FORM);
    }
  }, [navigate, canCreateProvider]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }
    setDeleteTarget(null);
  }, [deleteLoading]);

  const loadProviders = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllProviders(token);
      if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setRows([]);
        return;
      }

      const items =
        response?.data?.Providers ||
        response?.data?.providers ||
        response?.data ||
        response?.providers ||
        response;

      setRows(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error(error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoading(true);
      setDeleteError('');

      const providerId = getProviderRowId(deleteTarget);
      const response = await deleteProvider(token, providerId);

      if (response?.is_success) {
        await loadProviders();
        toast.success(response?.message || 'Provider deleted successfully.');
        setDeleteTarget(null);
      } else if (response?.status_code === 500) {
        setDeleteError('Internal Server Error. Please contact support.');
      } else {
        setDeleteError(response?.message || 'Failed to delete provider');
      }
    } catch (error) {
      console.error('Error deleting provider:', error);
      setDeleteError(error?.message || 'Failed to delete provider');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadProviders]);

  React.useEffect(() => {
    if (token) {
      loadProviders();
    }
  }, [token, loadProviders]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const rowStatus = getStatusName(row);
      const matchesStatus = status === 'All' || rowStatus === status;
      const matchesSearch =
        !term ||
        getProviderName(row).toLowerCase().includes(term) ||
        getOrganizationName(row).toLowerCase().includes(term) ||
        getClientName(row).toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(() => {
    const total = rows.length;
    const active = rows.filter((row) => getStatusName(row) === 'Active').length;
    const inactive = rows.filter((row) => getStatusName(row) === 'Inactive').length;

    return [
      {
        label: 'Total Providers',
        value: total,
        icon: <MdPeople size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active Providers',
        value: active,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Inactive Providers',
        value: inactive,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ];
  }, [rows]);

  const handleActionClick = React.useCallback(
    (action, row) => {
      const providerId = getProviderRowId(row);

      if (action === 'edit') {
        if (providerId) {
          navigate(APPRoutes.PROVIDERS_EDIT.replace(':id', providerId), { state: { provider: row } });
        } else {
          navigate(APPRoutes.PROVIDERS_FORM, { state: { provider: row } });
        }
        return;
      }

      if (action === 'view') {
        if (providerId) {
          navigate(APPRoutes.PROVIDERS_VIEW.replace(':id', providerId), { state: { provider: row } });
        } else {
          navigate(APPRoutes.PROVIDERS_FORM, { state: { provider: row } });
        }
        return;
      }

      if (action === 'delete') {
        handleDeleteClick(row);
        return;
      }

      console.info(`Provider action: ${action}`, row);
    },
    [navigate, handleDeleteClick],
  );

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'Provider',
        label: 'Provider',
        minWidth: 180,
        sortAccessor: (row) => getProviderName(row),
        filterAccessor: (row) => getProviderName(row),
        format: (_value, row) => canViewProvider ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{ font: 'inherit' }}
            onClick={() => handleActionClick('view', row)}
          >
            {getProviderName(row) || '—'}
          </button>
        ) : (
          <span className="text-dark">{getProviderName(row) || '—'}</span>
        ),
      },
      {
        id: 'Organization',
        label: 'Organization',
        minWidth: 180,
        sortAccessor: (row) => getOrganizationName(row),
        filterAccessor: (row) => getOrganizationName(row),
        format: (_value, row) => getOrganizationName(row) || <span className="text-secondary">—</span>,
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'Client',
        label: 'Client',
        minWidth: 180,
        sortAccessor: (row) => getClientName(row),
        filterAccessor: (row) => getClientName(row),
        format: (_value, row) => getClientName(row) || <span className="text-secondary">—</span>,
      });
    }

    cols.push(
      {
        id: 'Status',
        label: 'Status',
        minWidth: 90,
        align: 'center',
        sortAccessor: (row) => getStatusName(row),
        filterAccessor: (row) => getStatusName(row),
        format: (_value, row) => {
          const value = getStatusName(row);
          return (
            <span
              className="badge rounded-pill fw-semibold px-3 py-2 text-center"
              style={{
                minWidth: '96px',
                backgroundColor: 'var(--hc-status-neutral-bg, #f1f5f9)',
                color: 'var(--hc-table-footer-text, #475569)',
              }}
            >
              {value}
            </span>
          );
        },
      },
    );

    if (canViewProvider || canEditProvider || canDeleteProvider) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 100,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canViewProvider ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() => handleActionClick('view', row)}
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canEditProvider ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => handleActionClick('edit', row)}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
            {canDeleteProvider ? (
              <button
                type="button"
                title="Delete"
                className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                onClick={() => handleActionClick('delete', row)}
              >
                <MdDelete size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [
    handleActionClick,
    canViewProvider,
    canEditProvider,
    canDeleteProvider,
    isSuperAdmin,
  ]);

  return (
    <>
      <PageTitle title="Provider - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Providers Management</h4>
          <p className="text-secondary mb-0 small">
            Manage providers and review their organization and client assignments.
          </p>
        </div>
        {canCreateProvider ? (
          <button
            onClick={handleAddProvider}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Add Provider</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg">
              <div className="bg-white p-4 rounded-4 shadow">
                <div className="skeleton mb-2" style={{ height: 16, width: '60%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '40%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search provider, organization or client"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>
          <div className="col-12 col-md-3">
            <SearchableDropdown
              placeholder="Select status"
              options={STATUS_DROPDOWN_OPTIONS}
              value={status}
              onChange={setStatus}
              showSearch={true}
            />
          </div>
          <div className="col-12 mt-3">
            {loading ? (
              <div>
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="d-flex gap-3 align-items-center mb-3">
                    <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                  </div>
                ))}
              </div>
            ) : (
              <DataGrid
                columns={columns}
                rows={filteredRows}
                defaultRowsPerPage={10}
                stickyHeader
                getRowId={(row) => row.Id || row.ProviderId || row.id || row.provider_id || `${row.ProviderName}-${row.OrganizationName}-${row.ClientName}`}
              />
            )}
          </div>
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Provider"
        entityLabel="provider"
        itemName={getProviderName(deleteTarget) || ''}
        description="You are about to remove this provider from the platform. This action is permanent."
        confirmText="Delete Provider"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
